import * as React from 'react';
import * as Router from 'react-router-dom';
import { A, $, $$, T } from 'frontend';
import * as Hooks from 'frontend/hooks';

import { LinkDropbox } from './LinkDropbox';
import { Header, HeaderToolbarSlotContext } from './Header';
import { ListFiles } from './ListFiles';
import { ViewChopro } from './ViewChopro';
import { ViewPDF } from './ViewPDF';
import { ViewImage } from './ViewImage';
import { ViewMarkdown } from './ViewMarkdown';

import { Messages } from './Messages';
import { ensureNever, UnhandledCaseError } from 'frontend/utils';
import { Connect } from './Connect';
import { Settings } from './Settings';
import { useFilesIndex } from 'frontend/logic/files-index';

import { toFileStoreName } from 'frontend/logic/app-logic';
import { LanguageCoach } from './LanguageCoach';
import { ConnectFolder } from './ConnectFolder';
import { Music } from './Music';
import { PlaybackBar } from './Music/PlaybackBar';

import './App.css';
import { Onboarding } from './Onboarding';

function ListFilesRouter() {
  const currentFileStoreName = $$.getCurrentFileStoreName();
  const currentServer = $$.getCurrentServerOrNull();
  const servers = $$.getServers();
  const params = Router.useParams();
  const path = '/' + (params['*'] ?? '');
  const { fs } = params;
  const dispatch = Hooks.useDispatch();
  const navigate = Router.useNavigate();
  React.useEffect(() => {
    if (fs) {
      let fileStoreName = toFileStoreName(fs);
      let server: T.FileStoreServer | null = null;
      if (!fileStoreName) {
        server = servers.find((server) => server.id === fs) ?? null;
        if (server) {
          fileStoreName = 'server';
        }
      }
      if (!fileStoreName) {
        fileStoreName = currentFileStoreName;
        server = currentServer;
      }
      if (path === '/') {
        // Canonicalize the URL as you can represent root URLs in the simplest form as
        // "/browser/folder/" is a valid URL, but it more complicated.
        if (server?.storeType === 'music') {
          dispatch(A.viewMusic(server, path));
        } else {
          dispatch(A.viewListFiles(fileStoreName, server, path));
        }
        navigate('/', { replace: true });
        return;
      }
      dispatch(A.viewListFiles(fileStoreName, server, path));
    } else {
      // At the root route, redirect music stores to the music view.
      if (currentServer?.storeType === 'music') {
        dispatch(A.viewMusic(currentServer, path));
      } else {
        dispatch(A.viewListFiles(currentFileStoreName, currentServer, path));
      }
    }
  }, [fs, path, currentFileStoreName, currentServer]);
  return null;
}

function MusicRouter() {
  const servers = $$.getServers();
  const params = Router.useParams();
  const path = '/' + (params['*'] ?? '');
  const { fs } = params;
  const dispatch = Hooks.useDispatch();
  React.useEffect(() => {
    const server = servers.find((s) => s.id === fs) ?? null;
    if (server?.storeType === 'music') {
      dispatch(A.viewMusic(server, path));
    }
  }, [fs, path]);
  return null;
}

function toValidLanguageCoachSection(value: unknown): T.LanguageCoachSection {
  if (typeof value === 'string') {
    const view = value as T.LanguageCoachSection;
    switch (view) {
      case 'home':
        return view;
      case 'study-list':
        return view;
      case 'reading':
        return view;
      case 'learned':
        return view;
      default:
        ensureNever(view);
    }
  }
  return 'home';
}

function LanguageCoachRouter() {
  const params = Router.useParams();
  const [urlParams] = Router.useSearchParams();
  const section = toValidLanguageCoachSection(urlParams.get('section'));
  const path = '/' + (params['*'] ?? '');
  const { dispatch, getState } = Hooks.useStore();

  // Determine the path to the root part of the language coach.
  // e.g. /French.coach/reading/File.txt -> /French.coach
  const pathParts = path.split('/');
  const coachPartIndex = pathParts.findIndex((pathPart) =>
    pathPart.endsWith('.coach'),
  );
  const coachPath = pathParts.slice(0, coachPartIndex + 1).join('/');

  React.useEffect(() => {
    const invalidateOldData = $.getLanguageCoachPath(getState()) !== coachPath;
    dispatch(A.viewLanguageCoach(coachPath, path, invalidateOldData));
  }, [path]);

  React.useEffect(() => {
    dispatch(A.setLanguageCoachSection(section, coachPath, path));
  }, [section, path]);

  return null;
}

function ViewChoproRouter() {
  const params = Router.useParams();
  const path = '/' + (params['*'] ?? '');
  const dispatch = Hooks.useDispatch();
  React.useEffect(() => {
    dispatch(A.viewFile(path));
  }, [path]);
  return null;
}

function ViewPDFRouter() {
  const params = Router.useParams();
  const path = '/' + (params['*'] ?? '');
  const dispatch = Hooks.useDispatch();
  React.useEffect(() => {
    dispatch(A.viewPDF(path));
  }, [path]);
  return null;
}

function ViewImageRouter() {
  const params = Router.useParams();
  const path = '/' + (params['*'] ?? '');
  const dispatch = Hooks.useDispatch();
  React.useEffect(() => {
    dispatch(A.viewImage(path));
  }, [path]);
  return null;
}

function ViewMarkdownRouter() {
  const params = Router.useParams();
  const path = '/' + (params['*'] ?? '');
  const dispatch = Hooks.useDispatch();
  React.useEffect(() => {
    dispatch(A.viewMarkdown(path));
  }, [path]);
  return null;
}

function SettingsRouter() {
  const dispatch = Hooks.useDispatch();
  React.useEffect(() => {
    dispatch(A.viewSettings());
  });
  return null;
}

function ConnectRouter() {
  const dispatch = Hooks.useDispatch();
  React.useEffect(() => {
    dispatch(A.viewConnect());
  });
  return null;
}

function ConnectFolderRouter() {
  const dispatch = Hooks.useDispatch();
  React.useEffect(() => {
    dispatch(A.viewConnectFolder());
  });
  return null;
}

export function App() {
  useFilesIndex();
  Hooks.useDetectFirstPointerInteraction();
  Hooks.useDocumentElementInputMode();
  Hooks.usePreventUnhandledFileDrop();
  return (
    <Router.BrowserRouter>
      <AppRoutes />
    </Router.BrowserRouter>
  );
}

export function AppRoutes() {
  return (
    <>
      <Router.Routes>
        <Router.Route path="/" element={<ListFilesRouter />} />
        <Router.Route path="settings" element={<SettingsRouter />} />
        <Router.Route path="connect" element={<ConnectRouter />} />
        <Router.Route path="dropbox" element={<ConnectRouter />} />
        <Router.Route path="connect-folder" element={<ConnectFolderRouter />} />
        <Router.Route
          path="add-storage-provider"
          element={<Router.Navigate to="/connect-folder" replace />}
        />
        <Router.Route path="/:fs/folder" element={<ListFilesRouter />}>
          <Router.Route path="*" element={<ListFilesRouter />} />
        </Router.Route>
        <Router.Route
          path="/:fs/language-coach"
          element={<LanguageCoachRouter />}
        >
          <Router.Route path="*" element={<LanguageCoachRouter />} />
        </Router.Route>
        <Router.Route path="/:fs/file" element={<ViewChoproRouter />}>
          <Router.Route path="*" element={<ViewChoproRouter />} />
        </Router.Route>
        <Router.Route path="/:fs/pdf" element={<ViewPDFRouter />}>
          <Router.Route path="*" element={<ViewPDFRouter />} />
        </Router.Route>
        <Router.Route path="/:fs/image" element={<ViewImageRouter />}>
          <Router.Route path="*" element={<ViewImageRouter />} />
        </Router.Route>
        <Router.Route path="/:fs/md" element={<ViewMarkdownRouter />}>
          <Router.Route path="*" element={<ViewMarkdownRouter />} />
        </Router.Route>
        <Router.Route path="/:fs/music" element={<MusicRouter />}>
          <Router.Route path="*" element={<MusicRouter />} />
        </Router.Route>
      </Router.Routes>
      <LinkDropbox>
        <MainView>
          <Views />
        </MainView>
      </LinkDropbox>
      <Messages />
    </>
  );
}

/**
 * The main view of the app with a header and view area.
 */
export function MainView(props: { children: any }) {
  // Mounted here, above the route switch, so music playback and its controls
  // survive navigating anywhere in the app (e.g. into the file tree).
  // PlaybackBar renders nothing while playback is idle.
  const showPlaybackBar = $$.getMusicPlaybackStatus() !== 'idle';
  const [toolbarSlot, setToolbarSlot] = React.useState<HTMLDivElement | null>(
    null,
  );
  return (
    <div
      className={`appView${showPlaybackBar ? ' appView-withPlaybackBar' : ''}`}
    >
      <HeaderToolbarSlotContext.Provider value={toolbarSlot}>
        <Header toolbarSlotRef={setToolbarSlot} />
        {props.children}
      </HeaderToolbarSlotContext.Provider>
      <PlaybackBar />
    </div>
  );
}

function Views() {
  const view = $$.getView();
  const path = $$.getPath();
  const key = (view ?? '') + path;
  switch (view) {
    case null:
      return null;
    case 'list-files':
      return (
        <Onboarding key={key}>
          <ListFiles />
        </Onboarding>
      );
    case 'view-file':
      return <ViewChopro key={key} />;
    case 'view-pdf':
      return <ViewPDF key={key} />;
    case 'view-image':
      return <ViewImage key={key} />;
    case 'view-markdown':
      return <ViewMarkdown key={key} />;
    case 'connect':
      return <Connect key={key} />;
    case 'settings':
      return <Settings key={key} />;
    case 'connect-folder':
      return <ConnectFolder key={key} />;
    case 'language-coach':
      return <LanguageCoach key={key} />;
    case 'music':
      return <Music key={key} />;
    default:
      throw new UnhandledCaseError(view, 'view');
  }
}
